import { GridContainer, HR, useIsWindowBiggerThan } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { Asset } from '@websrc/App/Components/Asset/Asset'
import { Button, BUTTON_WIDTH } from '@websrc/App/Components/Button/Button'
import { ProductInfoButtons } from '@websrc/App/Components/Details/ProductInfo/Buttons/ProductInfoButtons'
import { ProductInfoCallouts } from '@websrc/App/Components/Details/ProductInfo/Callouts/ProductInfoCallouts'
import { ProductInfoHeader } from '@websrc/App/Components/Details/ProductInfo/Header/ProductInfoHeader'
import { ProductInfoPrice } from '@websrc/App/Components/Details/ProductInfo/Price/ProductInfoPrice'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapProductBandHeroSpot } from '@websrc/DataMappers/mapSpot'
import clsx from 'clsx'
import { Link } from 'gatsby'
import { breakpoints, gridContainer, gridItem } from 'leds'
import React, { useContext } from 'react'

import { productBandContainer } from './SpotProductBandHero.css'

export interface SpotProductBandHeroProps {
  spot: ReturnType<typeof mapProductBandHeroSpot>['product_band_hero']
  imageLazy?: boolean
  spotIndex: number
}

export const SpotProductBandHero = ({ spot, imageLazy, spotIndex }: SpotProductBandHeroProps) => {
  const locale = useLocale()
  const t = useStaticText()
  const productPath = `/${locale}/products`
  const isWindowMediumOrLarger = useIsWindowBiggerThan(breakpoints.m)

  const { matomoPageTemplate } = useContext(AppProviderContext)

  const isEven = spotIndex % 2 === 0

  return (
    <GridContainer data-testid={'productBandHero'}>
      <Link
        onClick={() =>
          matomo.trackEvent(
            'engagement',
            matomoPageTemplate,
            ['featured product'],
            [spot.title],
            'block'
          )
        }
        to={`${productPath}${spot.url?.replace(productPath, '')}`}
      >
        <div className={productBandContainer._}>
          <div
            className={clsx(
              gridContainer(),
              productBandContainer.wrapper._,
              isEven ? productBandContainer.wrapper.even : productBandContainer.wrapper.uneven
            )}
          >
            <div
              className={clsx(
                gridItem({ columns: isWindowMediumOrLarger ? 'two' : 'one' }),
                productBandContainer.imageColumn._
              )}
            >
              <Asset
                lazyLoading={imageLazy}
                sizes={`(max-width: 900px) 400px, (max-width: 1400px) 500px, 750px`}
                srcSetWidths={[320, 400, 600, 900, 1200]}
                {...spot.thumbnail}
              />
            </div>
            <div
              className={clsx(
                gridItem({ columns: isWindowMediumOrLarger ? 'two' : 'one' }),
                productBandContainer.descriptionColumn._,
                isEven
                  ? productBandContainer.descriptionColumn.even
                  : productBandContainer.descriptionColumn.uneven
              )}
            >
              <ProductInfoHeader productNumber={spot.product_number} show={true} />
              <ProductInfoPrice
                className={productBandContainer.descriptionColumn.productInfoPrice}
                productDetails={spot}
                show={true}
              />
              <>
                <HR className={productBandContainer.descriptionColumn.hr} light={true} />
                {spot.callouts?.length > 0 && (
                  <>
                    <ProductInfoCallouts callouts={spot.callouts} hideTitle={true} limit={4} />
                    <HR
                      className={productBandContainer.descriptionColumn.hrBottom}
                      data-testid={'callouts'}
                      light={true}
                    />
                  </>
                )}
              </>

              <div className={productBandContainer.descriptionColumn.buttonContainer._}>
                <div className={productBandContainer.descriptionColumn.buttonContainer.buttonItem}>
                  <Button
                    color={'blue'}
                    colorMode={'light'}
                    size={'medium'}
                    text={t.spot.expand_spot}
                    type={'primary'}
                    width={BUTTON_WIDTH.Full}
                  />
                </div>
                <div className={productBandContainer.descriptionColumn.buttonContainer.buttonItem}>
                  <ProductInfoButtons
                    color={'blue'}
                    colorMode={'light'}
                    productDetails={spot}
                    quantity={1}
                    size={'medium'}
                    type={'secondary'}
                    width={BUTTON_WIDTH.Full}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </GridContainer>
  )
}
